import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface NotificationStats {
  sentThisWeek: number;
  pending: number;
  failed: number;
  activeTypes: string[];
}

export function useNotificationStats(businessId: string | undefined) {
  const [stats, setStats] = useState<NotificationStats>({ sentThisWeek: 0, pending: 0, failed: 0, activeTypes: [] });
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
      const [sentRes, pendingRes, failedRes, typesRes] = await Promise.all([
        supabase.from('pending_notifications').select('id', { count: 'exact', head: true })
          .eq('business_id', businessId).eq('status', 'sent').gte('sent_at', weekAgo)
          .abortSignal(ctrl.signal),
        supabase.from('pending_notifications').select('id', { count: 'exact', head: true })
          .eq('business_id', businessId).eq('status', 'pending')
          .abortSignal(ctrl.signal),
        supabase.from('pending_notifications').select('id', { count: 'exact', head: true })
          .eq('business_id', businessId).eq('status', 'failed')
          .abortSignal(ctrl.signal),
        supabase.from('pending_notifications').select('notification_subtype')
          .eq('business_id', businessId).eq('status', 'sent')
          .abortSignal(ctrl.signal),
      ]);
      const typeSet = new Set<string>();
      for (const n of typesRes.data ?? []) { if (n.notification_subtype) typeSet.add(n.notification_subtype); }
      if (mountedRef.current) {
        setStats({
          sentThisWeek: sentRes.count ?? 0,
          pending: pendingRes.count ?? 0,
          failed: failedRes.count ?? 0,
          activeTypes: Array.from(typeSet).sort(),
        });
      }
    } catch { /* */ }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  return { stats, loading, refresh: fetch };
}
